import type { VerrichtingenEntity } from '@ksyos/http';
import { datadogRum } from '@datadog/browser-rum';
import { Box, Card, Divider, FormParagraph, Link, PageLayout, Spinner, Text } from '@ksyos/design-system';
import { last, sortBy } from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { ConclusieDiagnoseOption } from '../../../../../common/enums/ConclusieDiagnoseOption';
import type { IReduxState } from '../../../../configureStore';
import { servicedeskPhoneNumber } from '../../../../../common/constants';
import { isConclusieDiagnoseOption } from '../../../../../common/enums/ConclusieDiagnoseOption';
import { PatientDataError } from '../../../../../common/enums/PatientDataError';
import { mijnKsyosClient } from '../../../../services/api/mijnKsyosClient';
import { WelcomeHero } from '../../molecules/welcomeHero/WelcomeHero';
import { OptometryStepsCard } from '../toCards/OptometryStepsCard';
import { ResultCard } from '../toCards/ResultCard';
import { ToGeneralInfoCard } from '../toCards/ToGeneralInfoCard';

type WelcomeProps = Readonly<{
    error?: PatientDataError;
}>;

export function NewWelcome(props: WelcomeProps) {
    const { error } = props;
    const patientName = useSelector((state: IReduxState) => state.authentication.user?.displayName);
    const [result, setResult] = useState<ConclusieDiagnoseOption | null | undefined>();
    const hasResult = isConclusieDiagnoseOption(result);

    useEffect(() => {
        datadogRum.setViewContextProperty('serviceGroupName', 'Oog algemeen');
        checkIsResultAvailable();
    }, []);

    return (
        <PageLayout
            noGutterOnMobile={true}
            width="small"
        >
            <Box padding={4}>
                <Text fontWeight="bold">Goededag {patientName ?? ''},</Text>
                <Text>Dit is je persoonlijke Ksyos-omgeving</Text>
                {error === PatientDataError.generalError && (
                    <FormParagraph>
                        <Text color={'warning'}>Er is iets fout gegaan</Text>
                        <Text>
                            De persoonlijke omgeving kon niet succesvol worden ingeladen. Neem voor hulp contact op met
                            de Ksyos servicedesk
                        </Text>
                        <Link
                            openInNewTab={true}
                            to={`tel:${servicedeskPhoneNumber}`}
                            variant="link"
                        >
                            {servicedeskPhoneNumber}
                        </Link>
                    </FormParagraph>
                )}
                {error === PatientDataError.noDataAvailable && (
                    <FormParagraph>Op dit moment zijn er geen (actieve) onderzoeken.</FormParagraph>
                )}
            </Box>
            <WelcomeHero
                imgUrlDesktop="/assets/images/thuisconsult-home-large.svg"
                imgUrlMobile="/assets/images/thuisconsult-home-small.svg"
            />
            {/* Result is undefined wanneer het component nog initialiseert */}
            {result === undefined ? (
                <Card>
                    <Card.Body>
                        <Box
                            alignItems={'center'}
                            display="flex"
                            justifyContent="center"
                            minHeight="50px"
                        >
                            <Spinner />
                        </Box>
                    </Card.Body>
                </Card>
            ) : (
                <Card>
                    <Card.Body>
                        {hasResult ? (
                            <ResultCard
                                isInitiallyOpen={true}
                                result={result}
                            />
                        ) : (
                            <OptometryStepsCard isInitiallyOpen={true} />
                        )}
                        <Divider spacing="small" />
                        <ToGeneralInfoCard />
                    </Card.Body>
                </Card>
            )}
        </PageLayout>
    );

    async function checkIsResultAvailable() {
        const verrichtingen = await mijnKsyosClient.verrichtingen.getVerrichtingen({
            query: { serviceGroupName: 'Oog algemeen' },
        });
        if (verrichtingen.status != 200) {
            throw new Error('Failed to fetch verrichtingen');
        }
        setResult(getResult(verrichtingen.body.verrichtingen));
    }
}

function getResult(verrichtingen: VerrichtingenEntity['verrichtingen']): ConclusieDiagnoseOption | null {
    const lastVerrichting = last(sortBy(verrichtingen, ['uitgevoerdDatumTijd']));

    if (
        lastVerrichting?.type === 'toc-diagnostic-report-consult-report' &&
        isConclusieDiagnoseOption(lastVerrichting.data.heb_je_een_diagnose)
    ) {
        return lastVerrichting.data.heb_je_een_diagnose;
    }
    if (
        lastVerrichting?.type === 'too-diagnostic-report-examination-report' &&
        isConclusieDiagnoseOption(lastVerrichting.data.conclusie_diagnose)
    ) {
        return lastVerrichting.data.conclusie_diagnose;
    }

    return null;
}
